/** The stock screen: where the shelf stands against its alert levels, and what to buy. */
const stock = {
  en: {
    title: "Stock levels",
    subtitle:
      "Every product against its alert level — what is running low, and what to bring next time you go to the supplier.",
    loadFailed: "Could not load your stock.",
    saveFailed: "Could not save that alert.",
    subtitleWarehouse:
      "Every product on the shelf and in the warehouse, against its alert levels — what to bring from the supplier, and what to move to the shelf.",
    tabs: { all: "All stock", list: "Shopping list" },
    search: "Search a product or brand…",
    allCategories: "All categories",
    onlyLow: "Only what is low",
    shopWide: "Products without their own level are low at {count} or fewer.",
    changeShopWide: "Change in Settings",
    columns: {
      product: "Product",
      inShop: "In shop",
      inWarehouse: "In warehouse",
      total: "Total",
      alertAt: "Alert at",
      warehouseAlertAt: "Warehouse alert at",
      status: "Status",
    },
    noWarehouseAlert: "none",
    move: "Move",
    count: {
      start: "Count the warehouse",
      hint: "Type what is in the warehouse for the products you counted. The difference is moved between the shop and the warehouse: what you add comes off the shop's count, what you remove goes back to it — or, if it is gone, you can say so and the total goes down. Leave a product empty to leave it as it is.",
      downAsk: { one: "{count} product has less than before. Where did the difference go?", other: "{count} products have less than before. Where did the difference go?" },
      downShop: "It went back to the shop",
      downLost: "It is lost or was miscounted",
      lostNote: "Recount: fewer than recorded",
      save: "Save the counts",
      saved: { one: "{count} product moved.", other: "{count} products moved." },
      failed: "Could not save the counts.",
    },
    transfer: {
      title: "Move stock",
      now: "In the shop: {shop} · In the warehouse: {warehouse}",
      direction: "Where to",
      to: { shop: "To the shop", warehouse: "To the warehouse" },
      quantity: "How many (up to {max})",
      all: "All",
      tooMany: "There are only {max} to move.",
      save: "Move",
      failed: "Could not move the stock.",
      done: {
        shop: "{count} × {name} moved to the shop.",
        warehouse: "{count} × {name} moved to the warehouse.",
      },
    },
    followShop: "shop level ({count})",
    alertPlaceholder: "{count}",
    alertHint: "Leave empty to follow the shop level",
    mute: "Do not alert for this product",
    unmute: "Alert for this product again",
    muted: "No alert",
    status: { out: "Sold out", low: "Low", ok: "OK", refill: "refill from the warehouse" },
    addStock: "＋ Stock",
    none: "No product matches.",
    list: {
      intro:
        "What is at or under its alert level. The suggestion is enough to bring the shelf back to twice the alert level.",
      introWarehouse:
        "What to move from the warehouse to the shelf, and what to buy. Something low in the shop but held in the warehouse is moved, not bought; the suggestion brings the shelf, or the warehouse, back to twice its alert level.",
      moveTitle: "Move to the shop",
      moveAll: "Move them all",
      moved: { one: "{count} product moved to the shop.", other: "{count} products moved to the shop." },
      toMove: "To move",
      buyTitle: "To buy",
      moveHeader: "To move to the shop",
      product: "Product",
      inShop: "In shop",
      suggested: "Suggested",
      empty: "Nothing is running low. 🎉",
      copy: "Copy the list",
      copied: "Copied. Paste it in a message.",
      print: "Print",
      remind: "Remind me before I go",
      header: "To buy",
      line: "{name} — {count} (have {stock})",
    },
  },
  fr: {
    title: "Niveaux de stock",
    subtitle:
      "Chaque produit face à son niveau d'alerte — ce qui s'épuise, et ce qu'il faut rapporter la prochaine fois chez le fournisseur.",
    loadFailed: "Impossible de charger votre stock.",
    saveFailed: "Impossible d'enregistrer cette alerte.",
    subtitleWarehouse:
      "Chaque produit en rayon et à l'entrepôt, face à ses niveaux d'alerte — ce qu'il faut rapporter du fournisseur, et ce qu'il faut remonter en rayon.",
    tabs: { all: "Tout le stock", list: "Liste d'achats" },
    search: "Rechercher un produit ou une marque…",
    allCategories: "Toutes les catégories",
    onlyLow: "Seulement ce qui est bas",
    shopWide: "Les produits sans niveau propre sont bas à {count} ou moins.",
    changeShopWide: "Modifier dans Paramètres",
    columns: {
      product: "Produit",
      inShop: "En boutique",
      inWarehouse: "À l'entrepôt",
      total: "Total",
      alertAt: "Alerte à",
      warehouseAlertAt: "Alerte entrepôt à",
      status: "État",
    },
    noWarehouseAlert: "aucune",
    move: "Déplacer",
    count: {
      start: "Compter l'entrepôt",
      hint: "Saisissez ce qu'il y a à l'entrepôt pour les produits que vous avez comptés. La différence est déplacée entre la boutique et l'entrepôt : ce que vous ajoutez est retiré du stock de la boutique, ce que vous retirez y retourne — ou, si c'est perdu, dites-le et le total baisse. Laissez vide un produit pour ne pas y toucher.",
      downAsk: { one: "{count} produit a moins qu'avant. Où est passée la différence ?", other: "{count} produits ont moins qu'avant. Où est passée la différence ?" },
      downShop: "Elle est retournée en boutique",
      downLost: "Elle est perdue ou mal comptée",
      lostNote: "Recomptage : moins que prévu",
      save: "Enregistrer les comptages",
      saved: { one: "{count} produit déplacé.", other: "{count} produits déplacés." },
      failed: "Impossible d'enregistrer les comptages.",
    },
    transfer: {
      title: "Déplacer du stock",
      now: "En boutique : {shop} · À l'entrepôt : {warehouse}",
      direction: "Vers où",
      to: { shop: "Vers la boutique", warehouse: "Vers l'entrepôt" },
      quantity: "Combien (jusqu'à {max})",
      all: "Tout",
      tooMany: "Il n'y en a que {max} à déplacer.",
      save: "Déplacer",
      failed: "Impossible de déplacer le stock.",
      done: {
        shop: "{count} × {name} déplacé(s) vers la boutique.",
        warehouse: "{count} × {name} déplacé(s) vers l'entrepôt.",
      },
    },
    followShop: "niveau de la boutique ({count})",
    alertPlaceholder: "{count}",
    alertHint: "Laissez vide pour suivre le niveau de la boutique",
    mute: "Ne pas alerter pour ce produit",
    unmute: "Alerter de nouveau pour ce produit",
    muted: "Sans alerte",
    status: { out: "Épuisé", low: "Bas", ok: "OK", refill: "à remonter de l'entrepôt" },
    addStock: "＋ Stock",
    none: "Aucun produit ne correspond.",
    list: {
      intro:
        "Ce qui est au niveau d'alerte ou en dessous. La suggestion ramène le rayon à deux fois le niveau d'alerte.",
      introWarehouse:
        "Ce qu'il faut remonter de l'entrepôt en rayon, et ce qu'il faut acheter. Un produit bas en boutique mais présent à l'entrepôt se déplace, il ne s'achète pas ; la suggestion ramène le rayon, ou l'entrepôt, à deux fois son niveau d'alerte.",
      moveTitle: "À remonter en boutique",
      moveAll: "Tout déplacer",
      moved: { one: "{count} produit remonté en boutique.", other: "{count} produits remontés en boutique." },
      toMove: "À déplacer",
      buyTitle: "À acheter",
      moveHeader: "À remonter en boutique",
      product: "Produit",
      inShop: "En boutique",
      suggested: "Suggéré",
      empty: "Rien ne manque. 🎉",
      copy: "Copier la liste",
      copied: "Copié. Collez-le dans un message.",
      print: "Imprimer",
      remind: "Me le rappeler avant d'y aller",
      header: "À acheter",
      line: "{name} — {count} (reste {stock})",
    },
  },
  ar: {
    title: "مستويات المخزون",
    subtitle:
      "كل منتج مقابل مستوى تنبيهه — ما يوشك على النفاد، وما تجلبه عند زيارتك التالية للمورّد.",
    loadFailed: "تعذّر تحميل مخزونك.",
    saveFailed: "تعذّر حفظ هذا التنبيه.",
    subtitleWarehouse:
      "كل منتج على الرف وفي المخزن مقابل مستويات تنبيهه — ما تجلبه من المورّد وما تنقله إلى الرف.",
    tabs: { all: "كل المخزون", list: "قائمة الشراء" },
    search: "ابحث عن منتج أو علامة…",
    allCategories: "كل الفئات",
    onlyLow: "المنخفض فقط",
    shopWide: "المنتجات بلا مستوى خاص تُعدّ منخفضة عند {count} أو أقل.",
    changeShopWide: "غيّره من الإعدادات",
    columns: {
      product: "المنتج",
      inShop: "في المتجر",
      inWarehouse: "في المخزن",
      total: "المجموع",
      alertAt: "التنبيه عند",
      warehouseAlertAt: "تنبيه المخزن عند",
      status: "الحالة",
    },
    noWarehouseAlert: "لا شيء",
    move: "نقل",
    count: {
      start: "عدّ المخزن",
      hint: "اكتب ما في المخزن للمنتجات التي عددتها. يُنقل الفرق بين المتجر والمخزن: ما تزيده يُخصم من عدّاد المتجر وما تنقصه يعود إليه — أو إن كان ضائعاً فقل ذلك وينقص المجموع. اترك المنتج فارغاً ليبقى كما هو.",
      downAsk: { one: "منتج واحد أقل من السابق. أين ذهب الفرق؟", two: "منتجان أقل من السابق. أين ذهب الفرق؟", few: "{count} منتجات أقل من السابق. أين ذهب الفرق؟", many: "{count} منتجاً أقل من السابق. أين ذهب الفرق؟", other: "{count} منتج أقل من السابق. أين ذهب الفرق؟" },
      downShop: "عاد إلى المتجر",
      downLost: "ضاع أو عُدّ خطأً",
      lostNote: "إعادة عدّ: أقل من المسجّل",
      save: "حفظ العدّ",
      saved: { one: "نُقل منتج واحد.", two: "نُقل منتجان.", few: "نُقلت {count} منتجات.", many: "نُقل {count} منتجاً.", other: "نُقل {count} منتج." },
      failed: "تعذّر حفظ العدّ.",
    },
    transfer: {
      title: "نقل مخزون",
      now: "في المتجر: {shop} · في المخزن: {warehouse}",
      direction: "إلى أين",
      to: { shop: "إلى المتجر", warehouse: "إلى المخزن" },
      quantity: "كم (حتى {max})",
      all: "الكل",
      tooMany: "لا يوجد سوى {max} للنقل.",
      save: "نقل",
      failed: "تعذّر نقل المخزون.",
      done: {
        shop: "نُقل {count} × {name} إلى المتجر.",
        warehouse: "نُقل {count} × {name} إلى المخزن.",
      },
    },
    followShop: "مستوى المتجر ({count})",
    alertPlaceholder: "{count}",
    alertHint: "اتركه فارغاً لاتباع مستوى المتجر",
    mute: "لا تنبّهني لهذا المنتج",
    unmute: "نبّهني لهذا المنتج من جديد",
    muted: "بلا تنبيه",
    status: { out: "نفد", low: "منخفض", ok: "جيد", refill: "يُعاد ملؤه من المخزن" },
    addStock: "＋ مخزون",
    none: "لا يوجد منتج مطابق.",
    list: {
      intro:
        "ما بلغ مستوى التنبيه أو نزل عنه. المقترح يعيد الرف إلى ضعف مستوى التنبيه.",
      introWarehouse:
        "ما تنقله من المخزن إلى الرف وما تشتريه. ما كان منخفضاً في المتجر وموجوداً في المخزن يُنقل ولا يُشترى؛ والمقترح يعيد الرف أو المخزن إلى ضعف مستوى تنبيهه.",
      moveTitle: "للنقل إلى المتجر",
      moveAll: "انقلها كلها",
      moved: { one: "نُقل منتج واحد إلى المتجر.", two: "نُقل منتجان إلى المتجر.", few: "نُقلت {count} منتجات إلى المتجر.", many: "نُقل {count} منتجاً إلى المتجر.", other: "نُقل {count} منتج إلى المتجر." },
      toMove: "للنقل",
      buyTitle: "للشراء",
      moveHeader: "للنقل إلى المتجر",
      product: "المنتج",
      inShop: "في المتجر",
      suggested: "المقترح",
      empty: "لا شيء ينقص. 🎉",
      copy: "نسخ القائمة",
      copied: "تم النسخ. الصقها في رسالة.",
      print: "طباعة",
      remind: "ذكّرني قبل ذهابي",
      header: "للشراء",
      line: "{name} — {count} (المتوفر {stock})",
    },
  },
};

export default stock;
